import { IconButton, Link, ValidationMessage } from '@schuettflix/react-components';
import { DeleteIcon, PlusIcon } from '@schuettflix/icons-react';
import { useTranslation } from 'react-i18next';
import { WeekDayFocusEvent } from '@/modules/location/components/schedule/WeekdaySelection.tsx';
import { useEmitter } from '@/shared/hooks/useEmitter';
import {
    FormScheduleTime,
    FormScheduleTimeSchema,
    ScheduleTimePicker,
} from '@/modules/location/components/schedule/ScheduleTimePicker.tsx';
import { z } from 'zod';
import { FieldError, FieldErrorsImpl, Merge } from 'react-hook-form';
import { WeekdayName } from '@/modules/location/domain/Weekday.ts';

export const missingStartKey = 'missingStartKey';
export const missingEndKey = 'missingEndKey';
export const missingWeekdayKey = 'missingWeekdayKey';

export const FormScheduleSchema = z
    .array(FormScheduleTimeSchema)
    .min(1)
    .refine(array => array.some(entry => entry.weekdays.length > 0), missingWeekdayKey)
    .superRefine((array, context) => {
        array.forEach((entry, index) => {
            if (entry.weekdays.length > 0) {
                if (entry.start === null) {
                    context.addIssue({
                        code: z.ZodIssueCode.custom,
                        message: missingStartKey,
                        path: [index, 'start'],
                    });
                }
                if (entry.end === null) {
                    context.addIssue({
                        code: z.ZodIssueCode.custom,
                        message: missingEndKey,
                        path: [index, 'end'],
                    });
                }
            }
        });
    });

export type FormSchedule = z.input<typeof FormScheduleSchema>;

type ScheduleSectionProps = {
    value: FormSchedule;
    onChange: (value: FormSchedule) => void;
    error?: Merge<FieldError, FieldErrorsImpl<FormSchedule>>;
};

export const ScheduleSection = (props: ScheduleSectionProps) => {
    const { t } = useTranslation();
    const focusEmitter = useEmitter<WeekDayFocusEvent>();

    const addRow = () => {
        props.onChange([
            ...props.value,
            {
                weekdays: [],
                start: null,
                end: null,
            },
        ]);
    };

    const getRootErrorMessage = (): string | undefined => {
        if (!props.error || typeof props.error !== 'object') return undefined;

        const typeTranslationMap: Record<string, string> = {
            too_small: t('location.openingTimes.error.tooSmall'),
        } as const;

        const messageTranslationMap: Record<string, string> = {
            missingWeekdayKey: t('location.openingTimes.error.tooSmall'),
        } as const;

        const fieldError = props.error as FieldError;
        return (
            typeTranslationMap[fieldError.type] ||
            messageTranslationMap[fieldError.message ? fieldError.message : ''] ||
            fieldError.message
        );
    };

    const getTimeErrorMessage = (index: number, fieldName: 'start' | 'end'): string | undefined => {
        if (!props.error || !Array.isArray(props.error) || !props.error[index] || !props.error[index][fieldName])
            return undefined;

        const message = props.error[index][fieldName].message;
        if (!message) return undefined;

        const translationMap: Record<'start' | 'end', Record<string, string>> = {
            start: { [missingStartKey]: t('location.openingTimes.error.startInvalidType') },
            end: { [missingEndKey]: t('location.openingTimes.error.endInvalidType') },
        } as const;

        return translationMap[fieldName][message] || message;
    };

    function removeRow(index: number) {
        props.onChange(props.value.filter((_, i) => i !== index));
    }

    const scheduleWithoutWeekdays = (current: FormSchedule, weekdays: WeekdayName[]): FormSchedule => {
        return current.map(item => ({
            ...item,
            weekdays: item.weekdays.filter(weekday => !weekdays.includes(weekday)),
        }));
    };

    const handleSchedulePickerChange = (scheduleTime: FormScheduleTime, index: number) => {
        const schedule = scheduleWithoutWeekdays(props.value, scheduleTime.weekdays);
        schedule[index] = scheduleTime;

        props.onChange(schedule);
    };

    return (
        <div className="space-y-2">
            <h3 className="font-copy-md-strong">{t('location.openingTimes.title')}</h3>
            <p className="font-copy-md">{t('location.openingTimes.description')}</p>

            <ValidationMessage message={getRootErrorMessage()}>
                <div className="@2xl:space-y-2 space-y-6">
                    {props.value.map((row, index) => (
                        <div className="flex gap-2" role="row" key={index}>
                            <div className="flex-1">
                                <ScheduleTimePicker
                                    rowIndex={index}
                                    value={row}
                                    onChange={newTime => {
                                        handleSchedulePickerChange(newTime, index);
                                    }}
                                    onRowChange={event => {
                                        focusEmitter.emit({
                                            weekday: event.currentWeekday,
                                            row: event.currentRow + (event.direction === 'next' ? 1 : -1),
                                        });
                                    }}
                                    focusEmitter={focusEmitter}
                                    startError={getTimeErrorMessage(index, 'start')}
                                    endError={getTimeErrorMessage(index, 'end')}
                                />
                            </div>
                            <div className="flex h-[48px] items-center">
                                <IconButton
                                    icon={DeleteIcon}
                                    size="sm"
                                    className="mx-0"
                                    onClick={() => removeRow(index)}
                                    disabled={props.value.length <= 1}
                                />
                            </div>
                        </div>
                    ))}
                </div>
            </ValidationMessage>

            {props.value.length < 7 && (
                <Link
                    tabIndex={0}
                    prefix={<PlusIcon size="xs" />}
                    className="font-copy-md-strong pt-1"
                    label={t('location.openingTimes.addLinkLabel')}
                    onClick={addRow}
                    onKeyDown={e => {
                        if (e.key === 'Enter' || e.key === ' ') {
                            addRow();
                        }
                    }}
                />
            )}
        </div>
    );
};
